import { config } from '../config';
import { getIdToken, silentRefresh, login } from './auth-client';

/**
 * API client wrapping fetch with JWT injection, correlation IDs, and 401 auto-refresh.
 * Follows FRONTEND_CONSOLE_GUIDE.md patterns adapted for EntraID admin console.
 */

// ─── Error Types ─────────────────────────────────────────────────────────────

/** Thrown when a network-level failure occurs (no response from server). */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

/** Structured API error response matching the backend error format. */
export interface ApiErrorDetail {
  readonly field: string;
  readonly reason: string;
}

export interface ApiErrorPayload {
  readonly code: string;
  readonly message: string;
  readonly details: readonly ApiErrorDetail[];
  readonly requestId: string;
}

export class ApiErrorResponse extends Error {
  readonly status: number;
  readonly payload: ApiErrorPayload;

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message);
    this.name = 'ApiErrorResponse';
    this.status = status;
    this.payload = payload;
  }
}

/**
 * Type guard to check if an error is an ApiErrorResponse with a client error status.
 */
export function isClientError(error: unknown): error is ApiErrorResponse {
  return error instanceof ApiErrorResponse && error.status >= 400 && error.status < 500;
}

// ─── In-flight GET Deduplication ─────────────────────────────────────────────

const inflightGets = new Map<string, { promise: Promise<unknown>; timestamp: number }>();
const DEDUP_WINDOW_MS = 100;

// ─── Core Fetch Wrapper ──────────────────────────────────────────────────────

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  retry = true,
): Promise<T> {
  const url = `${config.apiBaseUrl}${path}`;
  const correlationId = crypto.randomUUID();
  const idToken = getIdToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Correlation-Id': correlationId,
  };

  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? err.message : 'Network request failed',
    );
  }

  // Handle 401 — attempt token refresh and retry once
  if (response.status === 401 && retry) {
    const refreshed = await silentRefresh();
    if (refreshed) {
      return request<T>(method, path, body, false);
    }
    // Refresh failed — redirect to login
    login();
    throw new ApiErrorResponse(401, {
      code: 'AUTHENTICATION_ERROR',
      message: 'Session expired',
      details: [],
      requestId: correlationId,
    });
  }

  if (!response.ok) {
    let payload: ApiErrorPayload;
    try {
      const errorBody = await response.json();
      payload = errorBody.error ?? {
        code: 'INTERNAL_ERROR',
        message: response.statusText,
        details: [],
        requestId: correlationId,
      };
    } catch {
      payload = {
        code: 'INTERNAL_ERROR',
        message: response.statusText,
        details: [],
        requestId: correlationId,
      };
    }
    throw new ApiErrorResponse(response.status, payload);
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

// ─── Exported Methods ────────────────────────────────────────────────────────

/**
 * Performs a GET request with in-flight deduplication (100ms window).
 */
export async function get<T>(path: string, query?: Record<string, string>): Promise<T> {
  let fullPath = path;
  if (query) {
    const params = new URLSearchParams(query);
    fullPath = `${path}?${params.toString()}`;
  }

  // Deduplication: return existing in-flight request if within window
  const existing = inflightGets.get(fullPath);
  if (existing && Date.now() - existing.timestamp < DEDUP_WINDOW_MS) {
    return existing.promise as Promise<T>;
  }

  const promise = request<T>('GET', fullPath);
  inflightGets.set(fullPath, { promise, timestamp: Date.now() });

  try {
    const result = await promise;
    return result;
  } finally {
    // Clean up after resolution
    setTimeout(() => inflightGets.delete(fullPath), DEDUP_WINDOW_MS);
  }
}

/**
 * Performs a POST request.
 */
export async function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('POST', path, body);
}

/**
 * Performs a PUT request.
 */
export async function put<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PUT', path, body);
}

/**
 * Performs a PATCH request.
 */
export async function patch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PATCH', path, body);
}

/**
 * Performs a DELETE request.
 */
export async function del(path: string): Promise<void> {
  await request<void>('DELETE', path);
}
